export const privacyPolicy = `# Privacy Policy

**Last Updated: April 13, 2025**

## 1. Introduction

Welcome to Collabish ("we," "our," or "us"). We respect your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services (collectively, the "Service").

Please read this Privacy Policy carefully. By accessing or using our Service, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy. If you do not agree with our policies and practices, please do not use our Service.

## 2. Information We Collect

We collect several types of information from and about users of our Service:

### 2.1 Personal Information You Provide to Us:
- Name
- Email address
- Password (stored in encrypted form)
- Optional self-declared personal information (such as biography, interests, demographic information)
- Review content and ratings
- Messages exchanged through our platform's chat features
- Other information you choose to provide

### 2.2 Information From Third-Party Services:
- TikTok account information when you choose to login through TikTok
- This may include profile information, username, and other information you've made publicly available on TikTok or have authorized TikTok to share with us

### 2.3 Automatically Collected Information:
- Device information (such as IP address, browser type, operating system)
- Usage data (such as pages visited, time spent on pages, click-through data)
- Cookies and similar tracking technologies

## 3. How We Use Your Information

We may use the information we collect for various purposes, including:

- Providing, maintaining, and improving our Service
- Processing transactions and managing your account
- Fulfilling your requests for information or services
- Facilitating communication between users (such as between brands and creators)
- Personalizing your experience
- Sending notifications related to your account or activity
- Analyzing usage patterns to improve our Service
- Detecting, preventing, and addressing technical or security issues
- Complying with legal obligations

## 4. Information Sharing and Disclosure

We may share your personal information in the following situations:

### 4.1 With Your Consent:
We may share your information when you have given us permission to do so.

### 4.2 With Service Providers:
We may share your information with third-party vendors, service providers, and other business partners who need access to such information to carry out work on our behalf.

### 4.3 For Business Transfers:
If we are involved in a merger, acquisition, financing, or sale of business assets, your information may be transferred as part of that transaction.

### 4.4 For Legal Purposes:
We may disclose your information where required by law or if we believe that such action is necessary to:
- Comply with a legal obligation or process
- Protect and defend our rights or property
- Prevent or investigate possible wrongdoing
- Protect the personal safety of users or the public

### 4.5 With TikTok:
Information may be shared with TikTok as required by their API terms of service and policies.

## 5. Data Security

We implement appropriate technical and organizational measures to protect the security of your personal information. However, please understand that no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.

## 6. Your Privacy Rights

Depending on your location, you may have certain rights regarding your personal information:

### 6.1 Access and Updates:
You can access and update certain personal information through your account settings or by contacting us.

### 6.2 Data Deletion:
You may request deletion of personal information we hold about you, subject to certain exceptions provided by law.

### 6.3 Additional Rights:
Depending on your jurisdiction, you may have additional rights such as:
- Right to object to certain processing
- Right to restrict processing
- Right to data portability
- Right to withdraw consent
- Right to lodge a complaint with a supervisory authority

To exercise these rights, please contact us using the information provided at the end of this policy.

## 7. Children's Privacy

Our Service is not directed to children under the age of 13 (or higher age as required by local law). We do not knowingly collect personal information from children. If we learn that we have collected personal information from a child without parental consent, we will take steps to delete that information.

## 8. Third-Party Links and Services

Our Service may contain links to third-party websites, applications, or services that are not operated by us. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party services. We encourage you to review the privacy policies of any third-party services you visit.

## 9. Cookie Policy

We use cookies and similar tracking technologies to enhance your experience on our Service. A cookie is a small text file stored on your device that contains information about your browsing activity.

### 9.1 Types of Cookies We Use:
- **Essential cookies**: Necessary for the functioning of our Service
- **Preference cookies**: Enable us to remember your preferences

### 9.2 Managing Cookies:
Most web browsers allow you to control cookies through their settings. However, if you reject cookies, you may still use our Service, but your ability to use some features may be limited.

## 10. International Data Transfers

Your information may be transferred to — and maintained on — computers located outside your state, province, country, or other governmental jurisdiction. The data protection laws in these locations may differ from those in your jurisdiction.

By providing your information, you consent to this transfer, storing, or processing. We will take reasonable steps to ensure your data is treated securely and in accordance with this Privacy Policy.

## 11. Changes to This Privacy Policy

We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.

You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.

## 12. Contact Information

If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:

Collabish LLC

By using our Service, you acknowledge that you have read and understood this Privacy Policy and agree to its terms.`;

const potentialThingsToCollect = `
1.3. Automatically Collected Information:
- IP address
- Browser type
- Device information
- Operating system
- Date and time of visits
- Pages viewed
- Time spent on pages
- Referral URLs
`;
